import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import Has from '../../component/Permission/Has';
import { Alert } from '../../component/Alert/Alert';
import LoadingSpinner from '../../component/LoadingSpinner/LoadingSpinner';
import { getInternalRoles, saveInternalUser } from '../../service/UserService';
import { ROLE_INTERNAL_U_USER_ADMIN, G_ADMIN_USER } from '../../config/PermissionTypes';
import { useCompanyPermission } from '../../component/CompanySelection/CompanyContext';
import AdminUserInformation from '../../component/User/AdminUserInformation';
import InternalUserRoleInformation from '../../component/User/InternalUserRoleInformation';
import ToggleSwitch from '../../component/ToggleSwitch/ToggleSwitch';



export default function AdminAddUser(props) {
    const [user, setUser] = useState({
        username: "",
        customerId: "",
        isActive: true,
        internalRole: {
            roleUuid: ""
        },
        isSuper: false,
        name: ""
    });
    const [availableRoles, setAvailableRoles] = useState(null);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [savePending, setSavePending] = useState(false);
    const [banner, setBanner] = useState({
        show: false,
        type: "success",
        message: ""
    });
    const navigate = useNavigate();

    useEffect(() => {
        const roleDataHandler = (internalRoles) => {
            if (internalRoles) {
                internalRoles = internalRoles.sort((a, b) => a.roleName.localeCompare(b.roleName));
                setAvailableRoles(internalRoles);
            } else {
                showBanner("error", `An error occurred while retrieving internal roles`)
            }
            setIsLoading(false);
        };

        getInternalRoles(roleDataHandler);
    }, [])

    const isSaveDisabled = () => {
        const errorsExist = !_.isEmpty(errors);
        return !user.username || !user.name.replace(/\s/g, '').length || !user.customerId || errorsExist || savePending || !{...user}["internalRole"].roleUuid;
    }

    function handleUserUpdate(field, value) {
        let updatedUser = {...user};
        updatedUser[field] = value
        setUser(updatedUser);
    }

    const showBanner = (type, message) => {
        setBanner({
            show: true,
            type: type,
            message: message
        })
    }
    
    const handleSaveUser = async () => {
        setSavePending(true);
        await Promise.resolve(saveInternalUser(user))
            .then(() => {
                navigate("/system/users")
            })
            .catch(error => {
                let message = error.response.data? error.response.data.message : "An error occurred while trying to save the new user.";
                showBanner("error", message);
                setSavePending(false);
            });
    }

    //if permissions are not loaded, show loading spinner
    const companyPermission = useCompanyPermission();
    if (!companyPermission) {
        return (
            <div id="dashboard-loader">
                <LoadingSpinner blue />
            </div>
        );
    }

    return (
        <><Has isPageLevel={true} permission={ROLE_INTERNAL_U_USER_ADMIN}>
            <div className="container-fluid add-users-page" data-testid="add-users-page">
                <div className="cb-row page-title-area">
                    <div className="cb-col-xs-8 cb-col-lg-8 page-title">
                        <h1 data-testid="add-user-page-header-title">Add User</h1>
                    </div>
                </div>

                {isLoading ?
                <LoadingSpinner blue testId="add-users-loading-spinner" />
                : <>{banner.show && 
                    <Alert type={`${banner.type}`} handleOnClose={() =>  setBanner({ show: false, message: ""})} dataTestId='edit-user-notification'>
                        <div style={{ fontWeight: '500' }}>{banner.message}</div>
                    </Alert>}
                
                <div className="user-info-container" data-testid="user-info-fields">
                    <div className="sectionTitle">New User Information</div>
                    <AdminUserInformation 
                        userId={user.username}
                        customerId={user.customerId}
                        name={user.name}
                        errors={errors}
                        setErrors={setErrors}
                        handleFieldUpdate={handleUserUpdate}
                    />
                </div>

                <div className="role-container" data-testid="user-role-options">
                    { !user.isSuper && <InternalUserRoleInformation 
                        existingRoleUuid={user.internalRole.roleUuid}
                        availableRoles={availableRoles}
                        errors={errors}
                        setErrors={setErrors}
                        handleFieldUpdate={handleUserUpdate}
                    /> }
                    <Has permission={G_ADMIN_USER}>
                        <ToggleSwitch 
                                id="superUserToggle"
                                label="Super User"
                                dataTestId="superUserToggle"
                                checked={user.isSuper}
                                onChange={checked => {
                                    handleUserUpdate("isSuper", checked);
                                }}
                                small
                            />
                    </Has>
                </div>

                <div className="user-buttons" data-testid="add-user-buttons">
                    <Link to={"/system/users"}>
                        <button
                            type="button"
                            className="cb-btn cb-btn-outline-secondary"
                            data-testid="add-user-cancel"
                            disabled={false}
                            onClick={() => null}>
                            Cancel
                        </button>
                    </Link>
                    <button 
                        className="cb-btn cb-btn-primary" 
                        data-testid="add-user-submit" 
                        disabled={isSaveDisabled()}
                        onClick={() => handleSaveUser()}>
                        Add User
                    </button>
                </div>

                </>}
            </div>
        </Has></>
    );
}